import PropertyCases from './pages/PropertyCases';
import PenaltyAssessments from './pages/PenaltyAssessments';
import OtpCancellations from './pages/OtpCancellations';
import AccreditationApplicant from './pages/AccreditationApplicant';
import AccreditationReview from './pages/AccreditationReview';
import IntermentCalendar from './pages/IntermentCalendar';
import Interments from './pages/Interments';
import OwnershipTransfers from './pages/OwnershipTransfers';
import OwnershipHistory from './pages/OwnershipHistory';
import ProductionDocuments from './pages/ProductionDocuments';
import OtpReceipts from './pages/OtpReceipts';
import ProductionUsers from './pages/ProductionUsers';
import ProductionTemplates from './pages/ProductionTemplates';
import OtpQuotationQueue from './pages/OtpQuotationQueue';
import OtpQuotation from './pages/OtpQuotation';
import OtpSetup from './pages/OtpSetup';
import OtpPreparations from './pages/OtpPreparations';
import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './styles/theme.css';
import { ConfirmationProvider } from './context/ConfirmationContext';
import { ToastProvider } from './context/ToastContext';
import AppLayout from './components/layout/AppLayout';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import CommissionRules from './pages/CommissionRules';
import Clients from './pages/Clients';
import Sales from './pages/Sales';
import Reports from './pages/Reports';
import Payments from './pages/Payments';
import Settings from './pages/Settings';
import Agents from './pages/Agents';
import RefundPolicies from './pages/RefundPolicies';
import Login from './pages/Login';
import { api } from './lib/api';
import { AuthProvider } from './context/AuthContext';
import { SidebarProvider } from './context/SidebarContext';
import { ThemeProvider } from './context/ThemeContext';

import Users from './pages/Users';
// Portal pages
import PortalLogin from './pages/portal/PortalLogin';
import PortalSetupPassword from './pages/portal/PortalSetupPassword';
import PortalForgotPassword from './pages/portal/PortalForgotPassword';
import PortalResetPassword from './pages/portal/PortalResetPassword';
import PortalApp from './pages/portal/PortalApp';

// ─── Portal Routes (separate client auth) ──────────────────
const PortalRoutes: React.FC = () => {
  const [clientUser, setClientUser] = useState<any>(() => {
    const saved = localStorage.getItem('vop_client_user');
    return saved ? JSON.parse(saved) : null;
  });

  const handleClientLogin = useCallback((u: any) => {
    setClientUser(u);
    localStorage.setItem('vop_client_user', JSON.stringify(u));
  }, []);

  const handleClientLogout = useCallback(() => {
    setClientUser(null);
    localStorage.removeItem('vop_client_user');
  }, []);

  useEffect(() => {
    window.addEventListener('vop:client-unauthorized', handleClientLogout);
    return () => window.removeEventListener('vop:client-unauthorized', handleClientLogout);
  }, [handleClientLogout]);

  return (
    <Routes>
      {/* Public portal pages */}
      <Route path="/login" element={<PortalLogin onLogin={handleClientLogin} />} />
      <Route path="/setup-password" element={<PortalSetupPassword />} />
      <Route path="/forgot-password" element={<PortalForgotPassword />} />
      <Route path="/reset-password" element={<PortalResetPassword />} />

      {/* Protected portal pages */}
      <Route path="/*" element={
        clientUser
          ? <PortalApp user={clientUser} onLogout={handleClientLogout} />
          : <Navigate to="/portal/login" replace />
      } />
    </Routes>
  );
};

function App() {
  const [user, setUser] = useState<any>(() => {
    const saved = localStorage.getItem('vop_user');
    try { return saved ? JSON.parse(saved) : null; } catch { return null; }
  });

  const handleLogin = useCallback((u: any) => {
    setUser(u);
    localStorage.setItem('vop_user', JSON.stringify(u));
  }, []);

  const handleLogout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('vop_user');
  }, []);

  useEffect(() => {
    if (!user) return;
    api.auth.me().catch(handleLogout);
  }, [user, handleLogout]);

  useEffect(() => {
    window.addEventListener('vop:unauthorized', handleLogout);
    return () => window.removeEventListener('vop:unauthorized', handleLogout);
  }, [handleLogout]);

  return (
    <ThemeProvider>
    <Router>
      <ToastProvider>
        <Routes>
          <Route path="/portal/*" element={<PortalRoutes />} />
          <Route path="/accreditation" element={<AccreditationApplicant />} />
          <Route path="/*" element={user ? <ConfirmationProvider>
        <AuthProvider user={user} logout={handleLogout}>
        <SidebarProvider>
        <AppLayout>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/interments" element={<IntermentCalendar />} />
            <Route path="/accreditation-review" element={<AccreditationReview />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/inventory" element={<Inventory />} />
            <Route path="/sales" element={<Sales />} />
            <Route path="/sales/otp" element={<OtpPreparations />} />
            <Route path="/sales/otp/reviews" element={<OtpQuotationQueue />} />
            <Route path="/sales/otp/:draftId/setup" element={<OtpSetup />} />
            <Route path="/sales/otp/:draftId/quotation" element={<OtpQuotation />} />
            <Route path="/sales/property/:lotId/cases" element={<PropertyCases />} />
<Route path="/sales/otp/:draftId/penalties" element={<PenaltyAssessments />} />
<Route path="/sales/otp/:draftId/receipts" element={<OtpReceipts />} />
            <Route path="/sales/otp/:draftId/cancellations" element={<OtpCancellations />} />
            <Route path="/sales/ownership/:lotId/interments" element={<Interments />} />
            <Route path="/sales/ownership/:lotId/transfers" element={<OwnershipTransfers />} />
            <Route path="/sales/ownership/:lotId" element={<OwnershipHistory />} />
            <Route path="/sales/otp/:draftId/documents" element={<ProductionDocuments />} />
            <Route path="/rules" element={<CommissionRules />} />
            <Route path="/agents" element={<Agents />} />
            <Route path="/users" element={<Users />} />
            <Route path="/payments" element={<Payments />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/settings/templates" element={<ProductionTemplates />} />
            <Route path="/settings/users" element={<ProductionUsers />} />
            <Route path="/refund-policies" element={<RefundPolicies />} />
          </Routes>
        </AppLayout>
        </SidebarProvider>
        </AuthProvider>
        </ConfirmationProvider> : <Login onLogin={handleLogin} />} />
        </Routes>
      </ToastProvider>
    </Router>
    </ThemeProvider>
  );
}

export default App;
